import { store } from '../store';
import { handleUnauthorizedError } from './apiMiddleware';

export interface Storage {
  id?: string;
  _id?: string;
  name: string;
  type: 'warehouse' | 'store' | 'other';
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  contactPerson?: string;
  phone?: string;
  email?: string;
  stockImportTemplate?: {
    sheetName?: string;
    headerRow?: number;
    mapping?: {
      productCode?: string;
      barcode?: string;
      quantity?: string;
      status?: string;
      batchNumber?: string;
      expiryDate?: string;
    };
    statusMappings?: Array<{
      externalValue: string;
      internalStatus: 'marketable' | 'reserved' | 'defective';
    }>;
    ignoredColumns?: string[];
    sourceFileName?: string;
    updatedAt?: string;
  };
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/storage`;

export const getStorages = async (): Promise<Storage[]> => {
  try {
    const response = await fetch(API_URL, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data.map((s: any) => ({ 
      ...s, 
      id: typeof s._id === 'object' && s._id?.$oid ? s._id.$oid : (s._id || s.id) 
    })) : [];
  } catch (error) {
    console.error('Erreur lors de la récupération des entrepôts:', error);
    throw error;
  }
};

export const createStorage = async (payload: Partial<Storage>): Promise<Storage> => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return { ...data, id: data._id || data.id };
  } catch (error) {
    console.error('Erreur lors de la création de l\'entrepôt:', error);
    throw error;
  }
};

export const updateStorage = async (id: string, payload: Partial<Storage>): Promise<Storage> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return { ...data, id: data._id || data.id };
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de l'entrepôt ${id}:`, error);
    throw error;
  }
};

export const deleteStorage = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
  } catch (error) {
    console.error(`Erreur lors de la suppression de l'entrepôt ${id}:`, error);
    throw error;
  }
};
